import { useTourProgress, useUpdateTourStep, useDismissTour } from "@/hooks/use-organisation";
import { useRouter } from "@tanstack/react-router";

import { usePopupStore } from "@/stores/popup.store";
import {
  Activity,
  ChevronRight,
  ClipboardList,
  Files,
  Globe,
  Inbox,
  LayoutDashboard,
  Mailbox,
  Plug,
  Settings,
  Tag,
  X,
} from "lucide-react";
import { useState, useEffect } from "react";
import Joyride, {
  ACTIONS,
  CallBackProps,
  Events,
  EVENTS,
  Step as JoyrideStep,
  STATUS,
  TooltipRenderProps,
} from "react-joyride";
import { WelcomePopup } from "../popups/welcome-dialog";
import { Button } from "../ui/button";

type Step = JoyrideStep & {
  data?: {
    next?: string;
    previous?: string;
  };
};

const StepTitle = ({
  title,
  icon,
}: {
  title: string;
  icon: React.ReactNode;
}) => (
  <div className="font-semibold text-lg mb-1 flex items-center gap-2">
    <div className="size-8 rounded-lg border  flex items-center justify-center [&>svg]:size-4">
      {icon}
    </div>
    {title}
  </div>
);

const stepsConfig: Step[] = [
  {
    target: "body",
    content: (
      <>
        <StepTitle title="Welcome to InboxDoctor!" icon={<LayoutDashboard />} />
        <p className="text-sm text-muted-foreground">
          Your comprehensive email deliverability and performance monitoring
          platform. Let's take a quick tour to help you get started!
        </p>
      </>
    ),
    data: {
      key: "welcome",
    },
    placement: "center",
  },
  {
    target: 'a[aria-label="basic-tour-guide"][href="/dashboard"]',
    content: (
      <>
        <StepTitle title="Dashboard" icon={<LayoutDashboard />} />
        <p className="text-sm text-muted-foreground">
          The InboxDoctor Dashboard provides a centralized overview of your
          email performance metrics. View everything about your emails in one
          place!
        </p>
      </>
    ),
    placement: "right",
    data: { key: "dashboard" },
  },
  {
    styles: {
      options: {
        zIndex: 1000,
      },
    },
    target: 'a[aria-label="basic-tour-guide"][href="/mailboxes"]',
    content: (
      <>
        <StepTitle title="Mailboxes" icon={<Mailbox />} />
        <p className="text-sm text-muted-foreground">
          Connect and manage all your mailboxes and sender profiles and assign
          subscription plans to mailboxes all in one streamlined interface.
        </p>
      </>
    ),
    placement: "right",
    data: {
      key: "mailboxes",
    },
  },
  {
    target: 'a[aria-label="basic-tour-guide"][href="/postmaster"]',
    content: (
      <>
        <StepTitle title="Postmaster" icon={<Globe />} />
        <p className="text-sm text-muted-foreground">
          The Postmaster section enables integration with Google Postmaster to
          monitor domain performance and deliverability metrics at one place
          without leaving the platform.
        </p>
      </>
    ),
    placement: "right",
    data: { key: "postmaster" },
  },
  {
    target: 'a[aria-label="basic-tour-guide"][href="/domain-health"]',
    content: (
      <>
        <StepTitle title="Domain Health Test" icon={<Activity />} />
        <p className="text-sm text-muted-foreground">
          Domain Health Test conducts hundreds of tests across your domain,
          email, and network performance to ensure all systems are operational
          and performing at their best.
        </p>
      </>
    ),
    placement: "right",
    data: {
      key: "email-health",
    },
  },
  {
    target: 'a[aria-label="basic-tour-guide"][href="/placement-test"]',
    content: (
      <>
        <StepTitle title="Inbox Placement" icon={<Inbox />} />
        <p className="text-sm text-muted-foreground">
          The Inbox Placement Test evaluates where your emails land-inbox, spam,
          or elsewhere across major email provider for consistent inbox
          placement.
        </p>
      </>
    ),
    placement: "right",
    data: {
      key: "placement-test",
    },
  },
  {
    target: 'a[aria-label="basic-tour-guide"][href="/seed-list"]',
    content: (
      <>
        <StepTitle title="Seed List" icon={<ClipboardList />} />
        <p className="text-sm text-muted-foreground">
          The Seed List feature provides access to professional seed mailboxes
          to simulate and analyze email delivery performance.
        </p>
      </>
    ),
    placement: "right",
    data: {
      key: "seed-list",
    },
  },
  {
    target: 'a[aria-label="basic-tour-guide"][href="/templates"]',
    content: (
      <>
        <StepTitle title="Email Templates" icon={<Files />} />
        <p className="text-sm text-muted-foreground">
          Create stunning, high-performing email templates with personalization,
          structure analysis, and spam trigger detection to ensure your messages
          captivate and deliver.
        </p>
      </>
    ),
    placement: "right",
    data: { key: "templates" },
  },
  {
    target: 'a[aria-label="basic-tour-guide"][href="/labels"]',
    content: (
      <>
        <StepTitle title="Labels" icon={<Tag />} />
        <p className="text-sm text-muted-foreground">
          Labels helps you to categorize, templates, mailboxes, postmaster
          domains efficiently. Use them to tag and filter assets, simplifying
          management.
        </p>
      </>
    ),
    placement: "right",
    data: { key: "labels" },
  },
  {
    target: 'a[aria-label="basic-tour-guide"][href="/integration"]',
    content: (
      <>
        <StepTitle title="Integration" icon={<Plug />} />
        <p className="text-sm text-muted-foreground">
          The Integration page connects InboxDoctor with external tools like
          Google Postmaster for enhanced email monitoring.
        </p>
      </>
    ),
    placement: "right",
    data: {
      key: "integration",
    },
  },
  {
    target: 'div[aria-label="Settings-nav-group"]',
    content: (
      <>
        <StepTitle title="Settings" icon={<Settings />} />
        <p className="text-sm text-muted-foreground">
          Settings in InboxDoctor let you customize account preferences, manage
          subscriptions, and configure user access.
        </p>
      </>
    ),
    placement: "right",
    data: {
      key: "settings",
      next: "/mailboxes",
    },
    disableBeacon: true,
  },
  {
    target: 'a[aria-label="basic-tour-guide"][href="/mailboxes"]',
    content: (
      <>
        <StepTitle title="Start Now!" icon={<Mailbox />} />
        <p className="text-sm text-muted-foreground">
          Connect your mailbox and get started with optimizing your email
          deliverability.
        </p>
      </>
    ),
    placement: "right",
    data: {
      key: "mailboxes",
      previous: "/billing",
    },
    disableBeacon: true,
  },
  {
    target: 'button[aria-label="basic-tour-guide"][id="add-mailbox"]',
    content: (
      <>
        <StepTitle title="Connect Mailboxes" icon={<Mailbox />} />
        <p className="text-sm text-muted-foreground">
          Click here to add your first mailbox. You can connect multiple
          mailboxes, set up sender profiles, and manage your email sending
          infrastructure all in one place.
        </p>
      </>
    ),
    placement: "left",
    data: {
      key: "add-mailboxes",
    },
    disableBeacon: true,
  },
];

function CustomTooltip({
  backProps,
  continuous,
  index,
  isLastStep,
  primaryProps,
  skipProps,
  step,
  tooltipProps,
}: TooltipRenderProps) {
  return (
    <div
      {...tooltipProps}
      className="bg-background border-none max-w-[420px] min-w-[290px] overflow-hidden rounded-lg relative"
    >
      {!isLastStep && (
        <button
          type="button"
          className="absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          {...skipProps}
        >
          <X className="h-4 w-4" />
        </button>
      )}
      <div className="p-4">
        {step.title && <h3 className="text-foreground mb-4">{step.title}</h3>}
        {step.content && <div>{step.content}</div>}
      </div>
      <div className="bg-primary-100 p-4 pt-0">
        <div className="flex justify-between items-center gap-2">
          {index > 0 ? (
            <Button {...backProps} size="sm" variant="outline">
              Back
            </Button>
          ) : (
            <span></span>
          )}
          <Button {...primaryProps} size="sm">
            {isLastStep ? "Finish Tour" : continuous ? "Next" : "Close"}
            <ChevronRight />
          </Button>
        </div>
      </div>
    </div>
  );
}

const BasicTourGuide = () => {
  const router = useRouter();
  const { mutateAsync: updateTourStep } = useUpdateTourStep();
  const { mutate: dismissTour } = useDismissTour();
  const {
    data: tourProgress,
    isLoading,
    refetch: refetchTourProgress,
  } = useTourProgress();

  const [state, setState] = useState<{ stepIndex: number; run: boolean }>({
    stepIndex: tourProgress?.currentStep ?? 0,
    run: true,
  });
  const { isTourSkipped } = usePopupStore();

  // Automatically dismiss tour if all steps are skipped
  useEffect(() => {
    if (
      tourProgress &&
      !tourProgress.isTourDismissed &&
      tourProgress.skippedSteps &&
      tourProgress.totalSteps &&
      tourProgress.skippedSteps.length === tourProgress.totalSteps
    ) {
      dismissTour();
    }
  }, [tourProgress, dismissTour]);

  // Update handleJoyrideCallback to save progress
  const handleJoyrideCallback = async (data: CallBackProps) => {
    const {
      action,
      index,
      step: { data: { next, previous, key } = {} } = {},
      type,
      status,
    } = data;

    const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);

    if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
      // Need to set our running state to false, so we can restart if we click start again.
      setState((pre) => ({ ...pre, run: false, stepIndex: 0 }));

      // If tour is skipped, dismiss it
      if (status === STATUS.SKIPPED) {
        try {
          dismissTour();
        } catch (error) {
          console.error("Failed to dismiss tour:", error);
        }
      }

      // console.log({
      //   name: "product-guide",
      //   currentStepIndex: 0,
      //   completedSteps: {},
      //   isFullyCompleted: status === STATUS.FINISHED,
      //   isSkipped: status === STATUS.SKIPPED,
      // });
      //   saveProgressMutation.mutate({
      //     name: "product-guide",
      //     currentStepIndex: 0,
      //     completedSteps: {},
      //     isFullyCompleted: status === STATUS.FINISHED,
      //     isSkipped: status === STATUS.SKIPPED,
      //   });
    } else if (ACTIONS.CLOSE === action) {
      setState((pre) => ({ ...pre, run: false }));

      // When user closes the tour, dismiss it
      try {
        dismissTour();
      } catch (error) {
        console.error("Failed to dismiss tour:", error);
      }

      // console.log({
      //   name: "product-guide",
      //   currentStepIndex: nextStepIndex,
      //   completedSteps: {},
      //   isFullyCompleted: false,
      //   isSkipped: false,
      // });
    } else if (
      ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as Events[]).includes(type)
    ) {
      setState((prev) => ({ ...prev, stepIndex: nextStepIndex }));

      if (action === ACTIONS.NEXT && EVENTS.STEP_AFTER === type) {
        try {
          await updateTourStep({ step: index + 1, action: "complete" });
        } catch (error) {
          console.error("Failed to update tour step:", error);
        }
      }

      if (action === ACTIONS.NEXT && EVENTS.STEP_AFTER === type && next) {
        router.navigate({ to: next });
      }

      if (action === ACTIONS.PREV && EVENTS.STEP_AFTER === type && previous) {
        router.navigate({ to: previous });
      }
    }
  };

  if (isLoading) return null;

  // Don't show tour if it's dismissed
  if (tourProgress?.isTourDismissed) return null;

  return (
    <>
      {!tourProgress?.isSeenWelcomePopup ? (
        <WelcomePopup
          isOpen={isTourSkipped ? false : !tourProgress?.isSeenWelcomePopup}
          closePopup={refetchTourProgress}
        />
      ) : (
        !isTourSkipped && (
          <Joyride
            steps={stepsConfig}
            run={state.run}
            stepIndex={state.stepIndex}
            continuous
            callback={handleJoyrideCallback}
            tooltipComponent={CustomTooltip}
            // showSkipButton
            showProgress
            disableCloseOnEsc={false}
            disableOverlayClose={false}
            disableScrolling={true}
            styles={{
              buttonBack: {
                color: "#407ff3",
              },
              buttonNext: {
                backgroundColor: "#407ff3",
              },
              options: {
                zIndex: 1000,
              },
            }}
          />
        )
      )}
    </>
  );
};

export default BasicTourGuide;
